// ─────────────────────────────────────────────────────────────────────────
// insuranceRules.js
//
// Turns an insurance company row (Admin → Insurance Companies) into the
// rules patient registration has to follow. The same checks run on the
// backend in /SavePatientInformation, so the admin's settings hold even if
// a form is bypassed.
//
// The `insurance` table stores the "Claim Check Code Required" switch in
// more than one shape — 'Required' / 'Optional' from older rows, '1' / '0'
// from rows saved by the admin screen — so every reader goes through
// isCccRequired() instead of comparing strings itself.
// ─────────────────────────────────────────────────────────────────────────

export function isCccRequired(ins) {
  const v = String(ins?.ccc ?? '').trim().toLowerCase();
  return v === 'required' || v === '1' || v === 'true' || v === 'yes';
}

// "Count" fields are free text in the DB; only a positive whole number
// counts as a rule. Blank / 0 means "no length rule".
export function requiredLength(count) {
  const n = parseInt(String(count ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function isNonInsured(cardType) {
  return String(cardType ?? '').trim().toLowerCase() === 'non insured';
}

// Short summary of the rules for helper text under the inputs.
export function describeRules(ins) {
  if (!ins) return { ccc: '', membership: '' };
  const cccLen = requiredLength(ins.cccc);
  const memLen = requiredLength(ins.member_count);
  const cccParts = [];
  if (isCccRequired(ins)) cccParts.push('Required');
  if (cccLen) cccParts.push(`${cccLen} characters`);
  return {
    ccc:        cccParts.join(' · '),
    membership: memLen ? `${memLen} characters` : '',
  };
}

// Returns { ccc?, membershipNo? } error messages; empty object = valid.
export function validateInsuranceFields(ins, { cardType, ccc, membershipNo }) {
  const errors = {};
  const name   = ins?.name || cardType || 'this insurance';
  const cccVal = String(ccc ?? '').trim();
  const memVal = String(membershipNo ?? '').trim();

  if (!isNonInsured(cardType) && !memVal) {
    errors.membershipNo = 'Membership Number is required';
  }

  if (!ins) return errors;

  const memLen = requiredLength(ins.member_count);
  if (!errors.membershipNo && memVal && memLen && memVal.length !== memLen) {
    errors.membershipNo = `Membership Number for ${name} must be exactly ${memLen} characters (entered ${memVal.length})`;
  }

  const cccLen = requiredLength(ins.cccc);
  if (isCccRequired(ins) && !cccVal) {
    errors.ccc = `CCC is required for ${name} — enter it or generate it`;
  } else if (cccVal && cccLen && cccVal.length !== cccLen) {
    errors.ccc = `CCC for ${name} must be exactly ${cccLen} characters (entered ${cccVal.length})`;
  }

  return errors;
}
